import type {
    CachedData,
    SerializedColumnFilterInvoices,
    SerializedColumnFilterTransactions,
    SortDirection,
    SortFieldInvoices,
} from '../types'
import { SortFieldTransactions } from '~/features/transactions/logic'
import {
    parseQueryString,
    setQueryString,
    removeQueryStringParams,
} from './querystrings'

/**
 * Query string keys for search state parameters
 */
const QUERY_KEYS = {
    // Global search
    SEARCH: 'search',
    DATE_FROM: 'dateFrom',
    DATE_TO: 'dateTo',

    // Invoice table filters
    VENDOR_NAME: 'vendorName',
    REASON: 'reason',
    INVOICE_ID: 'invoiceId',
    AMOUNT_MIN: 'amountMin',
    AMOUNT_MAX: 'amountMax',
    FILTER_DATE_FROM: 'filterDateFrom',
    FILTER_DATE_TO: 'filterDateTo',
    CREATED_AT_FROM: 'createdAtFrom',
    CREATED_AT_TO: 'createdAtTo',

    // Sorting
    SORT_FIELD: 'sortField',
    SORT_DIRECTION: 'sortDirection',
} as const

/**
 * Serialize search state to query string parameters
 */
export function serializeSearchStateToQueryString(
    searchState: CachedData['searchState'],
): Record<string, string | undefined> {
    if (!searchState) {
        return {}
    }

    const params: Record<string, string | undefined> = {}

    // Global search state
    if (searchState.global) {
        if (searchState.global.search) {
            params[QUERY_KEYS.SEARCH] = searchState.global.search
        }
        if (searchState.global.date) {
            if (searchState.global.date.from) {
                params[QUERY_KEYS.DATE_FROM] = searchState.global.date.from
                    .toISOString()
                    .split('T')[0]
            }
            if (searchState.global.date.to) {
                params[QUERY_KEYS.DATE_TO] = searchState.global.date.to
                    .toISOString()
                    .split('T')[0]
            }
        }
    }

    // Invoice table search state
    if (searchState.invoicesTable) {
        const columnFilters = searchState.invoicesTable
            .columnFilters as SerializedColumnFilterInvoices
        const { sortField, sortDirection } = searchState.invoicesTable

        // Column filters
        if (columnFilters.vendorName) {
            params[QUERY_KEYS.VENDOR_NAME] = columnFilters.vendorName
        }
        if (columnFilters.reason) {
            params[QUERY_KEYS.REASON] = columnFilters.reason
        }
        if (columnFilters.invoiceId) {
            params[QUERY_KEYS.INVOICE_ID] = columnFilters.invoiceId
        }
        if (columnFilters.amount) {
            if (columnFilters.amount.min !== undefined) {
                params[QUERY_KEYS.AMOUNT_MIN] =
                    columnFilters.amount.min.toString()
            }
            if (columnFilters.amount.max !== undefined) {
                params[QUERY_KEYS.AMOUNT_MAX] =
                    columnFilters.amount.max.toString()
            }
        }
        if (columnFilters.date) {
            if (columnFilters.date.from) {
                params[QUERY_KEYS.FILTER_DATE_FROM] = columnFilters.date.from
                    .toISOString()
                    .split('T')[0]
            }
            if (columnFilters.date.to) {
                params[QUERY_KEYS.FILTER_DATE_TO] = columnFilters.date.to
                    .toISOString()
                    .split('T')[0]
            }
        }
        if (columnFilters.createdAt) {
            if (columnFilters.createdAt.from) {
                params[QUERY_KEYS.CREATED_AT_FROM] =
                    columnFilters.createdAt.from.toISOString().split('T')[0]
            }
            if (columnFilters.createdAt.to) {
                params[QUERY_KEYS.CREATED_AT_TO] = columnFilters.createdAt.to
                    .toISOString()
                    .split('T')[0]
            }
        }

        // Sorting
        if (sortField) {
            params[QUERY_KEYS.SORT_FIELD] = sortField
        }
        if (sortDirection) {
            params[QUERY_KEYS.SORT_DIRECTION] = sortDirection
        }
    }

    // Transactions table search state
    if (searchState.transactionsTable) {
        const columnFilters = searchState.transactionsTable
            .columnFilters as SerializedColumnFilterTransactions
        const { sortField, sortDirection } = searchState.transactionsTable

        // Column filters
        if (columnFilters.vendorName) {
            params[QUERY_KEYS.VENDOR_NAME] = columnFilters.vendorName
        }
        if (columnFilters.amount) {
            if (columnFilters.amount.min !== undefined) {
                params[QUERY_KEYS.AMOUNT_MIN] =
                    columnFilters.amount.min.toString()
            }
            if (columnFilters.amount.max !== undefined) {
                params[QUERY_KEYS.AMOUNT_MAX] =
                    columnFilters.amount.max.toString()
            }
        }
        if (columnFilters.date) {
            if (columnFilters.date.from) {
                params[QUERY_KEYS.FILTER_DATE_FROM] = columnFilters.date.from
                    .toISOString()
                    .split('T')[0]
            }
            if (columnFilters.date.to) {
                params[QUERY_KEYS.FILTER_DATE_TO] = columnFilters.date.to
                    .toISOString()
                    .split('T')[0]
            }
        }
        if (columnFilters.createdAt) {
            if (columnFilters.createdAt.from) {
                params[QUERY_KEYS.CREATED_AT_FROM] =
                    columnFilters.createdAt.from.toISOString().split('T')[0]
            }
            if (columnFilters.createdAt.to) {
                params[QUERY_KEYS.CREATED_AT_TO] = columnFilters.createdAt.to
                    .toISOString()
                    .split('T')[0]
            }
        }

        // Sorting
        if (sortField) {
            params[QUERY_KEYS.SORT_FIELD] = sortField
        }
        if (sortDirection) {
            params[QUERY_KEYS.SORT_DIRECTION] = sortDirection
        }
    }

    return params
}

/**
 * Get the active route from URL query string
 */
function getActiveRoute(): string | null {
    if (typeof window === 'undefined') {
        return null
    }

    const params = new URLSearchParams(window.location.search)
    const routesParam = params.get('routes')
    if (!routesParam) {
        return null
    }

    const routeIds = routesParam
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean)

    return routeIds[0] || null
}

/**
 * Deserialize query string parameters to search state
 */
export function deserializeQueryStringToSearchState():
    | CachedData['searchState']
    | null {
    const params = parseQueryString()

    if (Object.keys(params).length === 0) {
        return null
    }

    const searchState: CachedData['searchState'] = {}

    // Global search state
    const globalSearch = params[QUERY_KEYS.SEARCH]
    const dateFrom = params[QUERY_KEYS.DATE_FROM]
    const dateTo = params[QUERY_KEYS.DATE_TO]

    if (globalSearch || dateFrom || dateTo) {
        searchState.global = {}

        if (globalSearch) {
            searchState.global.search = globalSearch
        }

        if (dateFrom || dateTo) {
            searchState.global.date = {
                from: undefined,
                to: undefined,
            }
            if (dateFrom) {
                try {
                    searchState.global.date.from = new Date(
                        dateFrom + 'T00:00:00Z',
                    )
                } catch (e) {
                    console.warn('Invalid dateFrom in query string:', dateFrom)
                }
            }
            if (dateTo) {
                try {
                    searchState.global.date.to = new Date(dateTo + 'T23:59:59Z')
                } catch (e) {
                    console.warn('Invalid dateTo in query string:', dateTo)
                }
            }
        }
    }

    // Determine which table to apply filters to based on active route
    const activeRoute = getActiveRoute()
    const isTransactionsRoute = activeRoute === 'transactions'
    const isInvoicesRoute = activeRoute === 'invoices'

    // If we have invoice-specific fields, always use invoicesTable
    const reason = params[QUERY_KEYS.REASON]
    const invoiceId = params[QUERY_KEYS.INVOICE_ID]
    const hasInvoiceSpecificFields = reason || invoiceId

    // Determine target table: prefer invoices if invoice-specific fields exist,
    // otherwise use route-based determination
    const targetTable = hasInvoiceSpecificFields
        ? 'invoices'
        : isTransactionsRoute
          ? 'transactions'
          : isInvoicesRoute
            ? 'invoices'
            : 'invoices' // default to invoices for backward compatibility

    const vendorName = params[QUERY_KEYS.VENDOR_NAME]
    const amountMin = params[QUERY_KEYS.AMOUNT_MIN]
    const amountMax = params[QUERY_KEYS.AMOUNT_MAX]
    const filterDateFrom = params[QUERY_KEYS.FILTER_DATE_FROM]
    const filterDateTo = params[QUERY_KEYS.FILTER_DATE_TO]
    const createdAtFrom = params[QUERY_KEYS.CREATED_AT_FROM]
    const createdAtTo = params[QUERY_KEYS.CREATED_AT_TO]
    const sortField = params[QUERY_KEYS.SORT_FIELD]
    const sortDirection = params[QUERY_KEYS.SORT_DIRECTION] as
        | SortDirection
        | undefined

    if (
        vendorName ||
        reason ||
        invoiceId ||
        amountMin ||
        amountMax ||
        filterDateFrom ||
        filterDateTo ||
        createdAtFrom ||
        createdAtTo ||
        sortField ||
        sortDirection
    ) {
        if (targetTable === 'invoices') {
            searchState.invoicesTable = {
                columnFilters: {} as SerializedColumnFilterInvoices,
            }

            // Column filters
            if (vendorName) {
                ;(
                    searchState.invoicesTable
                        .columnFilters as SerializedColumnFilterInvoices
                ).vendorName = vendorName
            }
            if (reason) {
                ;(
                    searchState.invoicesTable
                        .columnFilters as SerializedColumnFilterInvoices
                ).reason = reason
            }
            if (invoiceId) {
                ;(
                    searchState.invoicesTable
                        .columnFilters as SerializedColumnFilterInvoices
                ).invoiceId = invoiceId
            }
            if (amountMin || amountMax) {
                searchState.invoicesTable.columnFilters.amount = {}
                if (amountMin) {
                    const min = parseFloat(amountMin)
                    if (!isNaN(min)) {
                        searchState.invoicesTable.columnFilters.amount.min = min
                    }
                }
                if (amountMax) {
                    const max = parseFloat(amountMax)
                    if (!isNaN(max)) {
                        searchState.invoicesTable.columnFilters.amount.max = max
                    }
                }
            }
            if (filterDateFrom || filterDateTo) {
                searchState.invoicesTable.columnFilters.date = {
                    from: undefined,
                    to: undefined,
                }
                if (filterDateFrom) {
                    try {
                        searchState.invoicesTable.columnFilters.date.from =
                            new Date(filterDateFrom + 'T00:00:00Z')
                    } catch (e) {
                        console.warn(
                            'Invalid filterDateFrom in query string:',
                            filterDateFrom,
                        )
                    }
                }
                if (filterDateTo) {
                    try {
                        searchState.invoicesTable.columnFilters.date.to =
                            new Date(filterDateTo + 'T23:59:59Z')
                    } catch (e) {
                        console.warn(
                            'Invalid filterDateTo in query string:',
                            filterDateTo,
                        )
                    }
                }
            }
            if (createdAtFrom || createdAtTo) {
                searchState.invoicesTable.columnFilters.createdAt = {
                    from: undefined,
                    to: undefined,
                }
                if (createdAtFrom) {
                    try {
                        searchState.invoicesTable.columnFilters.createdAt.from =
                            new Date(createdAtFrom + 'T00:00:00Z')
                    } catch (e) {
                        console.warn(
                            'Invalid createdAtFrom in query string:',
                            createdAtFrom,
                        )
                    }
                }
                if (createdAtTo) {
                    try {
                        searchState.invoicesTable.columnFilters.createdAt.to =
                            new Date(createdAtTo + 'T23:59:59Z')
                    } catch (e) {
                        console.warn(
                            'Invalid createdAtTo in query string:',
                            createdAtTo,
                        )
                    }
                }
            }

            // Sorting
            if (
                sortField &&
                [
                    'date',
                    'amount',
                    'vendorName',
                    'invoiceId',
                    'reason',
                    'createdAt',
                ].includes(sortField)
            ) {
                searchState.invoicesTable.sortField =
                    sortField as SortFieldInvoices
            }
            if (sortDirection && ['asc', 'desc'].includes(sortDirection)) {
                searchState.invoicesTable.sortDirection = sortDirection
            }
        } else if (targetTable === 'transactions') {
            searchState.transactionsTable = {
                columnFilters: {} as SerializedColumnFilterTransactions,
            }

            // Column filters
            if (vendorName) {
                ;(
                    searchState.transactionsTable
                        .columnFilters as SerializedColumnFilterTransactions
                ).vendorName = vendorName
            }
            if (amountMin || amountMax) {
                searchState.transactionsTable.columnFilters.amount = {}
                if (amountMin) {
                    const min = parseFloat(amountMin)
                    if (!isNaN(min)) {
                        searchState.transactionsTable.columnFilters.amount.min =
                            min
                    }
                }
                if (amountMax) {
                    const max = parseFloat(amountMax)
                    if (!isNaN(max)) {
                        searchState.transactionsTable.columnFilters.amount.max =
                            max
                    }
                }
            }
            if (filterDateFrom || filterDateTo) {
                searchState.transactionsTable.columnFilters.date = {
                    from: undefined,
                    to: undefined,
                }
                if (filterDateFrom) {
                    try {
                        searchState.transactionsTable.columnFilters.date.from =
                            new Date(filterDateFrom + 'T00:00:00Z')
                    } catch (e) {
                        console.warn(
                            'Invalid filterDateFrom in query string:',
                            filterDateFrom,
                        )
                    }
                }
                if (filterDateTo) {
                    try {
                        searchState.transactionsTable.columnFilters.date.to =
                            new Date(filterDateTo + 'T23:59:59Z')
                    } catch (e) {
                        console.warn(
                            'Invalid filterDateTo in query string:',
                            filterDateTo,
                        )
                    }
                }
            }
            if (createdAtFrom || createdAtTo) {
                searchState.transactionsTable.columnFilters.createdAt = {
                    from: undefined,
                    to: undefined,
                }
                if (createdAtFrom) {
                    try {
                        searchState.transactionsTable.columnFilters.createdAt.from =
                            new Date(createdAtFrom + 'T00:00:00Z')
                    } catch (e) {
                        console.warn(
                            'Invalid createdAtFrom in query string:',
                            createdAtFrom,
                        )
                    }
                }
                if (createdAtTo) {
                    try {
                        searchState.transactionsTable.columnFilters.createdAt.to =
                            new Date(createdAtTo + 'T23:59:59Z')
                    } catch (e) {
                        console.warn(
                            'Invalid createdAtTo in query string:',
                            createdAtTo,
                        )
                    }
                }
            }

            // Sorting
            if (
                sortField &&
                [
                    'date',
                    'amount',
                    'vendorName',
                    'referenceText',
                    'note',
                    'createdAt',
                    'match',
                    'category',
                ].includes(sortField)
            ) {
                searchState.transactionsTable.sortField =
                    sortField as SortFieldTransactions
            }
            if (sortDirection && ['asc', 'desc'].includes(sortDirection)) {
                searchState.transactionsTable.sortDirection = sortDirection
            }
        }
    }

    return Object.keys(searchState).length > 0 ? searchState : null
}

/**
 * Update the URL query string with the current search state
 */
export function updateQueryStringWithSearchState(
    searchState: CachedData['searchState'],
): void {
    if (typeof window === 'undefined') {
        return
    }

    const url = new URL(window.location.href)
    const searchStateParams = serializeSearchStateToQueryString(searchState)
    const searchStateKeys = Object.values(QUERY_KEYS)

    // Remove all existing search state params
    for (const key of searchStateKeys) {
        url.searchParams.delete(key)
    }

    // Add new search state params
    for (const [key, value] of Object.entries(searchStateParams)) {
        if (value !== undefined && value !== null && value !== '') {
            url.searchParams.set(key, value)
        }
    }

    // Update URL without reload (preserving all other query params)
    window.history.replaceState({}, '', url.toString())
}

/**
 * Clear all search state related query string parameters
 */
export function clearSearchStateFromQueryString(): void {
    const allKeys = Object.values(QUERY_KEYS)
    removeQueryStringParams(allKeys)
}
